// Penyedia paket projects (POC): stage and status labels, the deadline
// countdown, the jenis/metode/kualifikasi labels, and the Buat dengan
// Pluto prompt per checklist item.
import type { ChecklistItem, ConsultationStatus, JenisPengadaan, Kualifikasi, MetodePemilihan, PaketStage, Project } from "../api";
import { formatRupiah } from "../lib/format";

/** The four stages of the stepper, in order (ditutup is shown on Pelaksanaan Pemilihan, selesai past the last). */
export const STEPS: { stage: PaketStage; label: string }[] = [
  { stage: "persiapan_pemilihan", label: "Persiapan Dokumen Penawaran" },
  { stage: "pelaksanaan_pemilihan", label: "Pelaksanaan Pemilihan" },
  { stage: "pelaksanaan_kontrak", label: "Pelaksanaan Kontrak" },
  { stage: "serah_terima", label: "Serah Terima" },
];

/** The first stage of every paket (and the fallback for a missing stage). */
export const FIRST_STAGE: PaketStage = "persiapan_pemilihan";

export const STAGE_LABEL: Record<PaketStage, string> = {
  persiapan_pemilihan: "Persiapan Dokumen Penawaran",
  pelaksanaan_pemilihan: "Pelaksanaan Pemilihan",
  pelaksanaan_kontrak: "Pelaksanaan Kontrak",
  serah_terima: "Serah Terima",
  selesai: "Selesai",
  ditutup: "Ditutup",
};

/** One line on what each stage is about (under the stepper). */
export const STAGE_HINT: Record<PaketStage, string> = {
  persiapan_pemilihan: "Siapkan usaha dan akun, temukan paket, baca dokumennya, lalu putuskan ikut atau tidak.",
  pelaksanaan_pemilihan: "Penuhi syarat, susun dan unggah penawaran, pantau evaluasi. Di akhir tahap, tandai hasilnya: terpilih atau tidak terpilih.",
  pelaksanaan_kontrak: "Kelola kontrak: SPPBJ, tanda tangan, mutu, progres, perubahan, dan pembayaran.",
  serah_terima: "Pemeriksaan hasil, BAST, tagihan akhir, dan pemeliharaan bila ada.",
  selesai: "Paket selesai. Riwayat chat dan file tetap tersimpan di project ini.",
  ditutup: "Paket ditutup karena tidak terpilih. Catatan Anda tersimpan untuk paket berikutnya.",
};

/** The forward step from a stage with a plain Lanjut action (Pelaksanaan Pemilihan branches on the outcome). */
export const NEXT_STAGE: Partial<Record<PaketStage, PaketStage>> = {
  persiapan_pemilihan: "pelaksanaan_pemilihan",
  pelaksanaan_kontrak: "serah_terima",
  serah_terima: "selesai",
};

/** The one stage a paket may step back to. */
export const PREVIOUS_STAGE: Partial<Record<PaketStage, PaketStage>> = {
  pelaksanaan_pemilihan: "persiapan_pemilihan",
  pelaksanaan_kontrak: "pelaksanaan_pemilihan",
  serah_terima: "pelaksanaan_kontrak",
  selesai: "serah_terima",
  ditutup: "pelaksanaan_pemilihan",
};

/** The stage where the outcome (terpilih or tidak terpilih) is decided. */
export const DECISION_STAGE: PaketStage = "pelaksanaan_pemilihan";

export const JENIS_LABEL: Record<JenisPengadaan, string> = {
  barang: "Barang",
  pekerjaan_konstruksi: "Pekerjaan Konstruksi",
  jasa_konsultansi_konstruksi: "Jasa Konsultansi Konstruksi",
  jasa_konsultansi_nonkonstruksi: "Jasa Konsultansi Nonkonstruksi",
  jasa_lainnya: "Jasa Lainnya",
};

export const METODE_LABEL: Record<MetodePemilihan, string> = {
  e_purchasing: "E-Purchasing",
  pengadaan_langsung: "Pengadaan Langsung",
  penunjukan_langsung: "Penunjukan Langsung",
  tender_cepat: "Tender Cepat",
  tender_seleksi: "Tender/Seleksi",
};

export const KUALIFIKASI_LABEL: Record<Kualifikasi, string> = {
  pascakualifikasi: "Pascakualifikasi",
  prakualifikasi: "Prakualifikasi",
};

/** 1-based position of the stage on the stepper (ditutup sits on Pelaksanaan Pemilihan; selesai is past the last, 5). */
export function stepNumber(stage: PaketStage): number {
  if (stage === "selesai") return STEPS.length + 1;
  const at = stage === "ditutup" ? DECISION_STAGE : stage;
  return STEPS.findIndex((s) => s.stage === at) + 1;
}

export const CONSULT_STATUS_LABEL: Record<ConsultationStatus, string> = {
  baru: "Baru",
  ditugaskan: "Ditugaskan",
  selesai: "Selesai",
};

export function consultTone(status: ConsultationStatus): "neutral" | "info" | "success" {
  return status === "selesai" ? "success" : status === "ditugaskan" ? "info" : "neutral";
}

/** Whole days from today (local) to a YYYY-MM-DD date; negative when past. */
export function daysUntil(date: string, now: Date = new Date()): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86_400_000);
}

/** "12 hari lagi", "Besok", "Hari ini", "Lewat 3 hari". */
export function countdownText(days: number): string {
  if (days === 0) return "Hari ini";
  if (days === 1) return "Besok";
  if (days > 1) return `${days} hari lagi`;
  return `Lewat ${-days} hari`;
}

const dateFmt = new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

/** 20 November 2026, from YYYY-MM-DD (no timezone shift). */
export function formatDeadline(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  return dateFmt.format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))));
}

/** The paket's display name: its paket name, else the project name. */
export function paketTitle(project: Pick<Project, "name" | "paketName">): string {
  return project.paketName?.trim() || project.name;
}

/**
 * The prompt Buat dengan Pluto puts into the project chat composer for one
 * checklist item: a concrete ask about this step of this paket, built from
 * the step's tindakan and output bukti, that the user may adjust.
 */
export function plutoPrompt(item: Pick<ChecklistItem, "key" | "label" | "detail">, project: Pick<Project, "name" | "paketName" | "nilaiHps" | "instansi">): string {
  const p = `"${paketTitle(project)}"`;
  const hps = project.nilaiHps ? ` dengan HPS ${formatRupiah(project.nilaiHps)}` : "";
  const d = item.detail;
  if (!d) return `Bantu saya menyelesaikan "${item.label}" untuk paket ${p}${hps}.`;
  const tindakan = d.tindakan.replace(/[.\s]+$/, "");
  return `Bantu saya mengerjakan langkah "${item.label}" untuk paket ${p}${hps}: ${tindakan}. Output yang perlu saya siapkan: ${d.output}.`;
}
